import isEqual from "lodash/isEqual";
import type * as React from "react";

import type {
  OrgLlmConnectionRecord,
  OrgLlmModel,
  UpdateOrgModelListRequest,
} from "@/shared/domain/orgs";
import { isRobotoManaged } from "@/shared/domain/orgs/orgModelConfig";
import { RobotoConflictException } from "@/shared/http";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

/** Whether the model may be used at all; a switched-off model stays on the list but runs nothing. */
export function isEnabled(model: OrgLlmModel): boolean {
  return model.enabled !== false;
}

/** Whether members can pick the model in chat: enabled and in the chat picker. */
export function offeredInPicker(model: OrgLlmModel): boolean {
  return isEnabled(model) && model.in_picker !== false;
}

/** Where a model on the list runs: "Roboto", or the label of the org connection serving it. */
export function modelSourceLabel(
  model: OrgLlmModel,
  connections: readonly OrgLlmConnectionRecord[],
): string {
  if (isRobotoManaged(model)) {
    return "Roboto";
  }
  return (
    connections.find((c) => c.connection_id === model.connection_id)?.label ??
    "Removed connection"
  );
}

/** Where an own-connection model stands with the server's verify calls: it runs only once `verified`. */
export type OwnModelVerification =
  | { state: "verified"; at: string }
  | { state: "unverified" }
  | { state: "failed"; error: string };

/** The model's verification, or `undefined` for a Roboto-managed model, which the org does not verify. */
export function ownModelVerification(
  model: OrgLlmModel,
): OwnModelVerification | undefined {
  if (isRobotoManaged(model)) {
    return undefined;
  }
  const error = model.last_error ?? null;
  if (error !== null) {
    return { state: "failed", error };
  }
  const verifiedAt = model.last_verified_at ?? null;
  if (verifiedAt === null) {
    return { state: "unverified" };
  }
  return { state: "verified", at: verifiedAt };
}

/**
 * Whether saving the list verifies `model`: an own model that is new, or whose
 * provider model id, connection or capability overrides differ from its saved
 * entry. Mirrors the server's rule; Roboto-managed models are never verified.
 */
export function ownModelTestedOnSave(
  model: OrgLlmModel,
  savedModels: readonly OrgLlmModel[],
): boolean {
  if (isRobotoManaged(model)) {
    return false;
  }
  const saved = savedModels.find((m) => m.id === model.id);
  return (
    saved === undefined ||
    isRobotoManaged(saved) ||
    saved.model_id !== model.model_id ||
    saved.connection_id !== model.connection_id ||
    (saved.max_output_tokens ?? null) !== (model.max_output_tokens ?? null) ||
    !isEqual(saved.capability_overrides ?? {}, model.capability_overrides ?? {})
  );
}

/**
 * Why a model on the org's own connection cannot run right now for a reason
 * outside the model itself (own connections not enabled for the org, the
 * org's tier, or its connection), or `undefined` when none stops it. The
 * first of those reasons that applies is the one reported. Roboto-managed
 * models are never reported here. The model's own verification is
 * {@link ownModelVerification}'s: an own model runs only when both allow it.
 * Nothing falls back to another model, so an unavailable model is not
 * offered in chat.
 */
export function modelUnavailableReason(
  model: OrgLlmModel,
  connections: readonly OrgLlmConnectionRecord[],
  isPremium: boolean,
  ownConnectionsEnabled: boolean,
): string | undefined {
  if (isRobotoManaged(model)) {
    return undefined;
  }
  if (!ownConnectionsEnabled) {
    return "Own LLM connections are not enabled for this organization.";
  }
  if (!isPremium) {
    return "Models on your own connections need the premium tier.";
  }
  const connection = connections.find(
    (c) => c.connection_id === model.connection_id,
  );
  if (connection === undefined) {
    return "Its connection was removed.";
  }
  if (connection.status === "failing") {
    return `Connection “${connection.label}” is failing.`;
  }
  if (connection.status === "disabled") {
    return `Connection “${connection.label}” is disabled.`;
  }
  return undefined;
}

/**
 * Why the list cannot be saved, or `null` when it can. Mirrors the server's
 * rules (`validate_org_model_list`) so the common mistakes show before a
 * round trip; the server stays the authority.
 */
export function modelListError(models: readonly OrgLlmModel[]): string | null {
  if (models.length === 0) {
    return "Add at least one model.";
  }
  const ids = models.map((m) => m.id);
  if (new Set(ids).size !== ids.length) {
    return "Each model may be on the list only once.";
  }
  if (!models.some(offeredInPicker)) {
    return "At least one enabled model must be in the chat picker.";
  }
  const defaults = models.filter((m) => m.is_default);
  if (defaults.length !== 1) {
    return "Pick exactly one default model.";
  }
  if (!isEnabled(defaults[0])) {
    return "The default model must be enabled.";
  }
  if (defaults[0].in_picker === false) {
    return "The default model must be in the chat picker.";
  }
  const utilities = models.filter((m) => m.is_utility === true);
  if (utilities.length > 1) {
    return "Only one model can be the utility model.";
  }
  if (utilities.length === 1 && !isEnabled(utilities[0])) {
    return "The utility model must be enabled.";
  }
  return null;
}

/**
 * The `PUT` body for the list. A Roboto-managed entry carries only the org's
 * choices (the server fills the rest from the catalog; `label` and `model_id`
 * are sent because the wire shape requires them). An own entry is sent whole,
 * minus the verification fields the server owns. `listModified` is the
 * saved list's `modified`, so a list saved elsewhere since is not overwritten.
 */
export function modelListRequest(
  models: readonly OrgLlmModel[],
  listModified: string,
): UpdateOrgModelListRequest {
  return {
    expected_modified: listModified,
    models: models.map((m) => {
      const choices = {
        is_default: m.is_default,
        is_utility: m.is_utility === true,
        in_picker: m.in_picker !== false,
        enabled: isEnabled(m),
      };
      if (isRobotoManaged(m)) {
        return {
          id: m.id,
          label: m.label,
          description: "",
          model_id: "",
          connection_id: null,
          context_window_tokens: null,
          ...choices,
        };
      }
      return {
        id: m.id,
        label: m.label,
        description: m.description,
        model_id: m.model_id,
        connection_id: m.connection_id,
        context_window_tokens: m.context_window_tokens,
        max_output_tokens: m.max_output_tokens ?? null,
        capability_overrides: m.capability_overrides ?? {},
        ...choices,
      };
    }),
  };
}

/**
 * The list without the model `id`. A removed default passes to the nearest
 * enabled model in the chat picker after it, else the nearest before it; a
 * removed utility model leaves the utility role to Roboto's.
 */
export function withoutModel(
  models: readonly OrgLlmModel[],
  id: string,
): OrgLlmModel[] {
  const index = models.findIndex((m) => m.id === id);
  if (index === -1) {
    return [...models];
  }
  const remaining = models.filter((m) => m.id !== id);
  if (!models[index].is_default) {
    return remaining;
  }
  const successor =
    remaining.slice(index).find(offeredInPicker) ??
    remaining.slice(0, index).reverse().find(offeredInPicker);
  return remaining.map((m) => ({ ...m, is_default: m === successor }));
}

/** The list with `id` as its only utility model, or with none (`null`: Roboto's utility model). */
export function withUtilityModel(
  models: readonly OrgLlmModel[],
  id: string | null,
): OrgLlmModel[] {
  return models.map((m) => ({ ...m, is_utility: m.id === id }));
}

/** The list with `id` as its only default. */
export function withDefaultModel(
  models: readonly OrgLlmModel[],
  id: string,
): OrgLlmModel[] {
  return models.map((m) => ({ ...m, is_default: m.id === id }));
}

/**
 * The list with the model `id` switched on or off. Switching off the utility
 * model also leaves the utility role to Roboto's; the default cannot be
 * switched off ({@link modelListError} says so).
 */
export function withModelEnabled(
  models: readonly OrgLlmModel[],
  id: string,
  enabled: boolean,
): OrgLlmModel[] {
  return models.map((m) =>
    m.id !== id
      ? m
      : {
          ...m,
          enabled,
          is_utility: enabled ? m.is_utility : false,
        },
  );
}

/** A profile id for a new own model, from its label, not yet used on the list. */
export function newModelId(
  label: string,
  models: readonly OrgLlmModel[],
): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "model";
  const taken = new Set(models.map((m) => m.id));
  let id = base;
  for (let n = 2; taken.has(id); n += 1) {
    id = `${base}-${n}`;
  }
  return id;
}

/** Why a save of the list failed; a conflict means the list was saved elsewhere and has been reloaded. */
export function modelListSaveErrorMessage(error: unknown): React.ReactNode {
  if (error instanceof RobotoConflictException) {
    return "Someone else changed this list while you were editing it, so your change was not saved. The list now shows their change; make yours again.";
  }
  return userFacingErrorMessage(error);
}
